import 'swiper/css/pagination';

import clsx from 'clsx';
import {useCallback, useMemo} from 'react';
import {Pagination} from 'swiper/modules';
import {SwiperSlide} from 'swiper/react';

import Button from '@/components/Button/Button';
import SwiperCarousel from '@/components/Swiper/SwiperCarousel';
import type {TipsInstruction} from '@/services/content/tipsContent.types';
import eventMessagesService from '@/services/events/eventMessagesService';

import getSwiperOptions from '../getSwiperOptions';

type DetailViewProps = {
    instructions: TipsInstruction[];
    guideItemType?: string;
    onInstructionChange?: (instruction: TipsInstruction) => void;
};

const DetailView = ({instructions, guideItemType, onInstructionChange}: DetailViewProps) => {
    const sorted = useMemo(() => [...instructions].sort((a, b) => a.order - b.order), [instructions]);
    const hasMultipleSlides = sorted.length > 1;

    const handleSlideChange = useCallback(
        (swiper: {activeIndex: number}) => {
            if (onInstructionChange && sorted[swiper.activeIndex]) {
                onInstructionChange(sorted[swiper.activeIndex]);
            }
        },
        [onInstructionChange, sorted]
    );

    const handleSwiperInit = useCallback(
        (_swiper: unknown) => {
            if (onInstructionChange && sorted[0]) {
                onInstructionChange(sorted[0]);
            }
        },
        [onInstructionChange, sorted]
    );

    const handlePrimaryButtonClick = (action?: string, trackingValue?: string) => {
        eventMessagesService.trackAmplitude(trackingValue);

        if (action) {
            eventMessagesService.sendButtonAction(action, {
                guideItemType,
            });
        }
    };

    const swiperOptions = {
        ...getSwiperOptions(hasMultipleSlides),
        onSlideChange: handleSlideChange,
        onInit: handleSwiperInit,
        wrapperClass: 'flex-1',
    };

    return (
        <div className='flex flex-1'>
            <SwiperCarousel
                options={swiperOptions}
                modules={hasMultipleSlides ? [Pagination] : []}
                className={clsx(
                    'instruction-swiper flex w-full flex-1 flex-col',
                    hasMultipleSlides && 'instruction-carousel'
                )}
            >
                {sorted.map((step, i) => {
                    const {title, description, primaryButton} = step;
                    const {
                        text: buttonText,
                        alt: buttonAlt,
                        action: buttonAction,
                        trackingValue: buttonTrackingValue,
                    } = primaryButton ?? {};

                    return (
                        <SwiperSlide key={i} className='flex h-full w-full flex-col justify-end'>
                            <div
                                className={clsx(
                                    'w-full px-6 pt-8 text-left backdrop-blur-[2px]',
                                    hasMultipleSlides ? 'pb-[70px]' : 'pb-8'
                                )}
                            >
                                <h2 className='wrap-break-word mb-4 font-bold text-2xl'>{title}</h2>
                                <p
                                    className='wrap-break-word mb-0 text-base leading-relaxed'
                                    dangerouslySetInnerHTML={{__html: description ?? ''}}
                                />
                                {buttonText && (
                                    <Button
                                        className='mt-8'
                                        aria-label={buttonAlt || buttonText}
                                        onClick={() => handlePrimaryButtonClick(buttonAction, buttonTrackingValue)}
                                    >
                                        {buttonText}
                                    </Button>
                                )}
                            </div>
                        </SwiperSlide>
                    );
                })}
            </SwiperCarousel>
        </div>
    );
};

export default DetailView;
